import { useState } from 'react';
import { motion } from 'framer-motion';
import { Info, ExternalLink, AlertCircle } from 'lucide-react';

import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import { Input } from '../../ui/Input';
import { Button } from '../../ui/Button';
import { Badge } from '../../ui/Badge';

import { 
  PROJECT_CATEGORIES, 
  FUNDING_FOR_OPTIONS 
} from '../../../constants/index.js';

const BasicInfoStep = ({ formData, updateFormData, errors, setErrors }) => {
  const [showOtherInput, setShowOtherInput] = useState(formData.funding_for === 'others');

  // Category options with labels and descriptions
  const categoryOptions = [
    { value: PROJECT_CATEGORIES.MEDICAL, label: 'Medical Relief', icon: '🏥', description: 'Healthcare and medical assistance' },
    { value: PROJECT_CATEGORIES.EDUCATION, label: 'Education', icon: '📚', description: 'Educational initiatives and learning' },
    { value: PROJECT_CATEGORIES.TECHNOLOGY, label: 'Technology', icon: '💻', description: 'Tech innovation and development' },
    { value: PROJECT_CATEGORIES.ENVIRONMENT, label: 'Environment', icon: '🌱', description: 'Environmental and sustainability projects' },
    { value: PROJECT_CATEGORIES.COMMUNITY, label: 'Community', icon: '🏘️', description: 'Community development and support' },
    { value: PROJECT_CATEGORIES.ARTS, label: 'Arts & Culture', icon: '🎨', description: 'Creative and cultural projects' }
  ];

  // Funding for options
  const fundingOptions = [
    { 
      value: FUNDING_FOR_OPTIONS.PERSONAL, 
      label: 'Personal', 
      description: 'Raising funds for personal needs or circumstances' 
    },
    { 
      value: FUNDING_FOR_OPTIONS.APP_DEVELOPMENT, 
      label: 'App Development', 
      description: 'Funding for mobile or web application development' 
    },
    { 
      value: FUNDING_FOR_OPTIONS.OTHERS, 
      label: 'Others', 
      description: 'Specify what the fund is being raised for' 
    }
  ];

  const handleInputChange = (field, value) => {
    const updates = { [field]: value };
    
    // Handle funding_for changes
    if (field === 'funding_for') {
      setShowOtherInput(value === FUNDING_FOR_OPTIONS.OTHERS);
      if (value !== FUNDING_FOR_OPTIONS.OTHERS) {
        updates.funding_for_other = '';
      }
    }

    // Handle app listing changes
    if (field === 'is_app_listing' && !value) {
      updates.app_url = '';
    }

    updateFormData(updates);
  };

  const validateField = (field, value) => {
    const newErrors = { ...errors };

    switch (field) {
      case 'title':
        if (!value || value.length < 10) {
          newErrors.title = 'Title must be at least 10 characters';
        } else if (value.length > 100) {
          newErrors.title = 'Title cannot exceed 100 characters';
        } else if (!/^[a-zA-Z0-9\s\-.,!?&()]+$/.test(value)) {
          newErrors.title = 'Title contains invalid characters';
        } else {
          delete newErrors.title;
        }
        break;

      case 'funding_for_other':
        if (formData.funding_for === FUNDING_FOR_OPTIONS.OTHERS) {
          if (!value || value.length < 3) {
            newErrors.funding_for_other = 'Please specify what the fund is being raised for (min 3 characters)';
          } else if (value.length > 100) {
            newErrors.funding_for_other = 'Cannot exceed 100 characters';
          } else {
            delete newErrors.funding_for_other;
          }
        }
        break;

      case 'app_url':
        if (formData.is_app_listing && value) {
          try {
            new URL(value);
            delete newErrors.app_url;
          } catch {
            newErrors.app_url = 'Please enter a valid URL';
          }
        }
        break;

      default:
        break;
    }

    setErrors(newErrors);
  };

  return (
    <div className="space-y-6 max-w-full overflow-hidden px-1 sm:px-0">
      {/* Project Title */}
      <Card className="border-0 shadow-soft hover:shadow-medium transition-all duration-300">
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-xl sm:text-h4 font-heading text-neutral-900 flex items-center gap-2">
            📝 Project Title
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 space-y-4">
          <div>
            <label className="block text-base sm:text-p1-m text-neutral-900 mb-2">
              What's your project called? *
            </label>
            <Input
              type="text"
              value={formData.title}
              onChange={(e) => handleInputChange('title', e.target.value)}
              onBlur={(e) => validateField('title', e.target.value)}
              placeholder="Enter a compelling title for your project"
              maxLength={100}
              className={`h-11 sm:h-12 ${errors.title ? 'border-red-500 bg-red-50/30' : ''}`}
            />
            <div className="flex justify-between items-center mt-2">
              {errors.title ? (
                <div className="flex items-center gap-1.5 text-red-600 text-sm">
                  <AlertCircle className="w-4 h-4" />
                  <span>{errors.title}</span>
                </div>
              ) : (
                <p className="text-xs sm:text-p2-r text-neutral-500">
                  Make it clear and engaging ({formData.title.length}/100)
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Funding For */}
      <Card className="border-0 shadow-soft hover:shadow-medium transition-all duration-300">
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-xl sm:text-h4 font-heading text-neutral-900 flex items-center gap-2">
            🎯 Who/What is this for?
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 space-y-4">
          <div>
            <label className="block text-base sm:text-p1-m text-neutral-900 mb-3">
              Who or what is the fund being raised for? *
            </label>
            <div className="space-y-3">
              {fundingOptions.map((option) => (
                <motion.label
                  key={option.value}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  className={`flex items-start gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all duration-200 active:bg-neutral-50 ${
                    formData.funding_for === option.value
                      ? 'border-primary-teal bg-primary-teal/5'
                      : 'border-neutral-200 hover:border-primary-teal/50'
                  }`}
                >
                  <input
                    type="radio"
                    name="funding_for"
                    value={option.value}
                    checked={formData.funding_for === option.value}
                    onChange={(e) => handleInputChange('funding_for', e.target.value)}
                    className="w-5 h-5 text-primary-teal mt-0.5"
                  />
                  <div className="flex-1">
                    <div className="font-semibold text-neutral-900 text-sm sm:text-base">{option.label}</div>
                    <div className="text-xs sm:text-p2-r text-neutral-600 mt-0.5">{option.description}</div>
                  </div>
                </motion.label>
              ))}
            </div>

            {/* Custom input for "Others" */}
            {showOtherInput && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-4"
              >
                <Input
                  type="text"
                  value={formData.funding_for_other}
                  onChange={(e) => handleInputChange('funding_for_other', e.target.value)}
                  onBlur={(e) => validateField('funding_for_other', e.target.value)}
                  placeholder="Please specify (e.g., Community Center, etc.)"
                  maxLength={100}
                  className={`h-11 sm:h-12 ${errors.funding_for_other ? 'border-red-500 bg-red-50/30' : ''}`}
                />
                {errors.funding_for_other && (
                  <div className="flex items-center gap-1.5 text-red-600 mt-2 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    <span>{errors.funding_for_other}</span>
                  </div>
                )}
              </motion.div>
            )}

            {errors.funding_for && (
              <div className="flex items-center gap-1.5 text-red-600 mt-2 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{errors.funding_for}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Project Category */}
      <Card className="border-0 shadow-soft hover:shadow-medium transition-all duration-300">
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-xl sm:text-h4 font-heading text-neutral-900 flex items-center gap-2">
            📂 Project Category
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 space-y-4">
          <div>
            <label className="block text-base sm:text-p1-m text-neutral-900 mb-3">
              Which category best describes your project? *
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {categoryOptions.map((category) => (
                <motion.label
                  key={category.value}
                  whileHover={{ scale: 1.01 }}
                  whileTap={{ scale: 0.99 }}
                  className={`flex items-center gap-3 p-4 border-2 rounded-xl cursor-pointer transition-all duration-200 active:bg-neutral-50 ${
                    formData.category === category.value
                      ? 'border-primary-teal bg-primary-teal/5'
                      : 'border-neutral-200 hover:border-primary-teal/50'
                  }`}
                >
                  <input
                    type="radio"
                    name="category"
                    value={category.value}
                    checked={formData.category === category.value}
                    onChange={(e) => handleInputChange('category', e.target.value)}
                    className="sr-only"
                  />
                  <div className="text-2xl flex-shrink-0">{category.icon}</div>
                  <div className="flex-1">
                    <div className="font-semibold text-neutral-900 text-sm sm:text-base">{category.label}</div>
                    <div className="text-xs sm:text-p2-r text-neutral-600 leading-tight">{category.description}</div>
                  </div>
                </motion.label>
              ))}
            </div>

            {errors.category && (
              <div className="flex items-center gap-1.5 text-red-600 mt-2 text-sm">
                <AlertCircle className="w-4 h-4" />
                <span>{errors.category}</span>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* App Listing */}
      <Card className="border-0 shadow-soft hover:shadow-medium transition-all duration-300">
        <CardHeader className="p-4 sm:p-6">
          <CardTitle className="text-xl sm:text-h4 font-heading text-neutral-900 flex items-center gap-2">
            📱 App Listing
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 sm:p-6 space-y-4">
          <div>
            <label className="flex items-start gap-3 cursor-pointer group active:bg-neutral-50 p-1 rounded-lg transition-colors">
              <div className="relative flex items-center">
                <input
                  type="checkbox"
                  checked={formData.is_app_listing}
                  onChange={(e) => handleInputChange('is_app_listing', e.target.checked)}
                  className="w-5 h-5 text-primary-teal border-neutral-300 rounded focus:ring-primary-teal transition-all cursor-pointer"
                />
              </div>
              <div className="flex-1">
                <span className="text-base sm:text-p1-m font-medium text-neutral-900 group-hover:text-primary-teal transition-colors">
                  This is an app listing
                </span>
                <p className="text-xs sm:text-p2-r text-neutral-600 mt-0.5">
                  Check this if you're submitting a mobile or web application for funding
                </p>
                {formData.is_app_listing && (
                  <Badge variant="default" className="mt-2 bg-primary-teal/10 text-primary-teal hover:bg-primary-teal/20 border-0">
                    App Project
                  </Badge>
                )}
              </div>
            </label>

            {/* App URL Input */}
            {formData.is_app_listing && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: 'auto' }}
                exit={{ opacity: 0, height: 0 }}
                className="mt-4"
              >
                <label className="block text-sm sm:text-p1-m font-medium text-neutral-900 mb-2">
                  App URL *
                </label>
                <div className="relative">
                  <Input
                    type="url"
                    value={formData.app_url}
                    onChange={(e) => handleInputChange('app_url', e.target.value)}
                    onBlur={(e) => validateField('app_url', e.target.value)}
                    placeholder="https://your-app.com or store link"
                    className={`pl-3 pr-10 h-11 sm:h-12 ${errors.app_url ? 'border-red-500 bg-red-50/30' : ''}`}
                  />
                  {formData.app_url && !errors.app_url && (
                    <Button
                      variant="ghost"
                      size="sm"
                      className="absolute right-1 top-1/2 -translate-y-1/2 p-2 h-9 w-9 text-neutral-400 hover:text-primary-teal"
                      onClick={() => window.open(formData.app_url, '_blank')}
                    >
                      <ExternalLink className="w-4 h-4" />
                    </Button>
                  )}
                </div>
                {errors.app_url && (
                  <div className="flex items-center gap-1.5 text-red-600 mt-2 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    <span>{errors.app_url}</span>
                  </div>
                )}
              </motion.div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Info Banner */}
      <Card className="bg-blue-50 border-blue-200 border-2">
        <CardContent className="p-4">
          <div className="flex items-start gap-3">
            <Info className="w-5 h-5 text-blue-600 mt-0.5 flex-shrink-0" />
            <div className="text-p2-r text-blue-800">
              <p className="font-semibold mb-1">Next Steps</p>
              <p>After completing the basic information, you'll be able to tell your story, set your funding goal, and add more details about your project.</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default BasicInfoStep;
